/**
 * Idiomas — vocabulário único (24/09/2026)
 *
 * `contatos.idioma` passou a guardar o código BCP-47 que vem de `paises.idioma`
 * ('pt-BR', 'es-AR', 'en-US'). Antes guardava NOME ('PT-BR', 'Espanhol', 'Ingles').
 *
 * O front comparava `idioma !== "PT-BR"` literal em 8 lugares. Com o vocabulário
 * novo, 'pt-BR' minúsculo passava em todos eles: o ChatAdmin passou a chamar
 * `/traduzir_mensagens` para TODO contato brasileiro — e essa rota SOBRESCREVE
 * `chats.mensagem` com a saída do LLM. Daqui pra frente a decisão é sempre por
 * `precisaTraduzir()`, nunca por string literal.
 *
 * As funções aceitam o vocabulário novo E o antigo, porque contato ainda não
 * migrado e bundle em cache no navegador continuam mandando 'Espanhol'/'PT-BR'.
 */

export const IDIOMA_PADRAO = "pt-BR";

const NOME_POR_PREFIXO: { [k: string]: string } = {
  am: "Amárico", ar: "Árabe", az: "Azerbaijano", be: "Bielorrusso",
  bg: "Búlgaro", bn: "Bengali", bs: "Bósnio", cs: "Tcheco",
  da: "Dinamarquês", de: "Alemão", el: "Grego", en: "Inglês",
  es: "Espanhol", et: "Estoniano", fa: "Persa", fi: "Finlandês",
  fil: "Filipino", fr: "Francês", he: "Hebraico", hi: "Híndi",
  hr: "Croata", hu: "Húngaro", hy: "Armênio", id: "Indonésio",
  is: "Islandês", it: "Italiano", ja: "Japonês", ka: "Georgiano",
  km: "Khmer", ko: "Coreano", lt: "Lituano", lv: "Letão",
  mk: "Macedônio", mn: "Mongol", ms: "Malaio", mt: "Maltês",
  my: "Birmanês", nb: "Norueguês", ne: "Nepali", nl: "Neerlandês",
  pl: "Polonês", ps: "Pashto", pt: "Português", ro: "Romeno",
  ru: "Russo", si: "Cingalês", sk: "Eslovaco", sl: "Esloveno",
  sq: "Albanês", sr: "Sérvio", sv: "Sueco", sw: "Suaíli",
  th: "Tailandês", tr: "Turco", uk: "Ucraniano", ur: "Urdu",
  uz: "Uzbeque", vi: "Vietnamita", zh: "Chinês",
};

/** Vocabulário antigo (nome solto) -> código canônico. */
const CODIGO_LEGADO: { [k: string]: string } = {
  "pt-br": "pt-BR", pt: "pt-BR", portugues: "pt-BR", "português": "pt-BR",
  es: "es-ES", espanhol: "es-ES",
  en: "en-US", ingles: "en-US", "inglês": "en-US",
  de: "de-DE", alemao: "de-DE", "alemão": "de-DE",
  fr: "fr-FR", frances: "fr-FR", "francês": "fr-FR",
  it: "it-IT", italiano: "it-IT",
  ja: "ja-JP", japones: "ja-JP", "japonês": "ja-JP",
  zh: "zh-CN", chines: "zh-CN", "chinês": "zh-CN",
};

/** Devolve o código BCP-47 canônico ('es-AR'), ou null se não der pra identificar. */
export function normalizarIdioma(valor?: string | null): string | null {
  const v = (valor || "").trim();
  if (!v) return null;
  const k = v.toLowerCase();
  if (CODIGO_LEGADO[k]) return CODIGO_LEGADO[k];
  const partes = k.replace(/_/g, "-").split("-");
  if (NOME_POR_PREFIXO[partes[0]]) {
    return partes.length > 1 && partes[1]
      ? `${partes[0]}-${partes[1].toUpperCase()}`
      : partes[0];
  }
  return null;
}

/**
 * False para pt-BR (a língua de origem), vazio ou código desconhecido.
 * pt-PT / pt-AO CONTINUAM traduzindo: são variantes de destino.
 */
export function precisaTraduzir(valor?: string | null): boolean {
  const cod = normalizarIdioma(valor);
  return cod !== null && cod !== IDIOMA_PADRAO;
}

/** 'es-AR' -> 'Espanhol'. Cai no próprio valor se o prefixo for desconhecido. */
export function nomeDoIdioma(valor?: string | null): string {
  const cod = normalizarIdioma(valor) || (valor || "").trim();
  return NOME_POR_PREFIXO[cod.split("-")[0].toLowerCase()] || cod;
}

/** Opções do seletor de idioma do contato, no vocabulário novo. */
export const OPCOES_IDIOMA: { valor: string; rotulo: string }[] = [
  { valor: "pt-BR", rotulo: "Português" },
  { valor: "en-US", rotulo: "Inglês" },
  { valor: "es-ES", rotulo: "Espanhol" },
  { valor: "de-DE", rotulo: "Alemão" },
  { valor: "fr-FR", rotulo: "Francês" },
];

/**
 * Valor a exibir no `<select>`. Normaliza o que veio do banco para que um
 * contato ainda gravado como 'Espanhol' case com a opção 'es-ES'.
 */
export function valorDoSeletor(valor?: string | null): string {
  return normalizarIdioma(valor) || IDIOMA_PADRAO;
}

/**
 * Opções do seletor já incluindo o idioma atual do contato.
 *
 * O contato pode ter um código regional que não está na lista curada — 'es-AR'
 * vindo de `paises.ddi`, por exemplo. Sem isto o `<select>` não teria a opção
 * correspondente e mostraria "Português", mentindo sobre o cadastro.
 */
export function opcoesParaContato(valor?: string | null) {
  const cod = normalizarIdioma(valor);
  if (!cod || OPCOES_IDIOMA.some((o) => o.valor === cod)) return OPCOES_IDIOMA;
  return [...OPCOES_IDIOMA, { valor: cod, rotulo: `${nomeDoIdioma(cod)} (${cod})` }];
}
